import { AlertTriangle } from "lucide-react";
import { RunnerBreakdown } from "@/components/analysis/RunnerBreakdown";
import { RoughieDetail } from "@/components/analysis/RoughieDetail";

const Pick = ({ label, x, testid }) => (
  <div className="text-sm" data-testid={testid}>
    <p><span className="text-[11px] uppercase tracking-widest text-blue-300 mr-2">{label}</span>
      <span className="font-bold">{x.number}. {x.horse}</span>
      {x.reason && <span className="text-slate-400"> — {x.reason}</span>}</p>
    <RoughieDetail s={{ ...x, role: label === "Roughie / value" ? "roughie" : "pick" }} testid={`${testid}-detail`} />
  </div>
);

const Picks = ({ p }) => (
  <div className="rounded-xl border border-white/5 p-3 space-y-2" data-testid="analysis-picks">
    {p.top_pick && <Pick label="Top pick" x={p.top_pick} testid="analysis-pick-top" />}
    {p.danger && <Pick label="Danger" x={p.danger} testid="analysis-pick-danger" />}
    {p.roughie ? <Pick label="Roughie / value" x={p.roughie} testid="analysis-pick-roughie" />
      : <p className="text-xs text-fuchsia-300" data-testid="analysis-no-roughie">{p.no_roughie_reason}</p>}
  </div>
);

const List = ({ title, items = [], testid }) =>
  items.filter(Boolean).length ? (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">{title}</p>
      <ul className="list-disc pl-5 text-xs text-slate-300 space-y-0.5">{items.filter(Boolean).map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  ) : null;

export const AnalysisDraft = ({ d }) => (
  <div className="space-y-4" data-testid="analysis-draft">
    {d.model && <p className="text-xs font-semibold text-blue-300" data-testid="analysis-model">Written by {{ claude: "Claude", gemini: "Gemini", chatgpt: "ChatGPT" }[d.model] || d.model}</p>}
    {d.insufficient_evidence && (
      <p className="flex gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3" data-testid="analysis-insufficient">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />The AI found the evidence insufficient to support a confident selection.
      </p>
    )}
    {["verified", "missing", "outdated"].some((k) => d.data_check?.[k]?.length) && <div className="grid sm:grid-cols-3 gap-4 rounded-xl border border-white/5 p-3">
      <List title="Verified facts used" items={d.data_check?.verified} testid="analysis-verified" />
      <List title="Missing information" items={d.data_check?.missing} testid="analysis-missing" />
      <List title="Possibly outdated" items={d.data_check?.outdated} testid="analysis-outdated" />
    </div>}
    {d.race_shape && <p className="text-sm text-slate-300"><span className="text-[11px] uppercase tracking-widest text-violet-300 mr-2">Race shape (view)</span>{d.race_shape}</p>}
    <div className="space-y-3">{(d.runners || []).map((r) => <RunnerBreakdown key={r.number} r={r} />)}</div>
    {d.comparison && <div><p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Comparison</p><p className="text-sm text-slate-300">{d.comparison}</p></div>}
    {d.investigate_further?.length > 0 && (
      <div data-testid="analysis-investigate">
        <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Investigate further</p>
        <ul className="space-y-1 text-sm">{d.investigate_further.map((x, i) => <li key={i}><span className="font-bold">{x.number}. {x.horse}</span> <span className="text-slate-400">— {x.why}</span></li>)}</ul>
      </div>
    )}
    {d.picks && <Picks p={d.picks} />}
    {d.recommendation && (
      <div className="rounded-xl bg-blue-600/10 border border-blue-500/30 p-3" data-testid="analysis-ai-recommendation">
        <p className="text-[11px] uppercase tracking-widest text-blue-300 mb-1">AI recommendation (for your review)</p>
        <p className="text-sm text-slate-200">{d.recommendation}</p>
      </div>
    )}
  </div>
);
